import { getDisplayData, getBestWeekStats } from "./leagueUtils";
import { PLAYOFF_TEAMS } from "./season";
import { ordinal } from "./leagueStatus";
import { getGameDisplayInfo, formatPeriod, getGameSpreadDisplay, shouldShowSpread } from "./gameStatus";

// What a live league card shows on /home: a short table (the top three, then
// the viewer if they sit lower), a ticker of things happening in the league
// this week, and the viewer's own five starters.
//
// Pure, so it can be tested without a database. useUserLeagues supplies the
// rows it already loads; nothing here reads anything new.
//
// Movement is this week's: each member's rank now against their rank with this
// week's points taken off, which is the table as it stood when the week began.
// That avoids reading weekly_standings, which holds nothing until a week has
// been snapshotted.


/**
 * @param {object[]} members   league_members rows for one league, each with
 *                             { id, user_id, points, team_name, avatar_url, users }
 * @param {object[]} lineupRows weekly_lineups rows for those members, all weeks
 * @param {string}   viewerId  users.id of the person looking
 * @param {number}   week      the current lineup week
 * @param {object}   tripPlayByMember  memberId -> { school } for live 5x Plays
 * @param {object[]} moves     recent move_history rows, newest first, with
 *                             { member_id, added, dropped, created_at } and the
 *                             team slugs already resolved to school names
 * @param {object}   gamesBySlug  this week's games, from buildGamesBySlug
 * @param {object}   teams     the AuthContext teams map
 * @param {Date}     now
 */
export function buildLeagueCard({ members = [], lineupRows = [], viewerId, week, tripPlayByMember = {}, moves = [], gamesBySlug = {}, teams = {}, now = new Date() }) {
  const names = managerNames(members);
  const rowsByMember = {};
  lineupRows.forEach((row) => {
    (rowsByMember[row.member_id] = rowsByMember[row.member_id] || []).push(row);
  });

  const enriched = members.map((m) => {
    const rows = rowsByMember[m.id] || [];
    const { bestWeekPoints, bestWeekCaptainPoints } = getBestWeekStats(rows);
    const weekRow = rows.find((r) => r.week === week);
    return {
      ...m,
      bestWeekPoints,
      bestWeekCaptainPoints,
      teamName: m.team_name || "Unnamed Team",
      weekPoints: weekRow?.points ?? 0,
    };
  });

  const current = getDisplayData({ viewMode: "current", members: enriched });
  // The table as the week began. The best-week tiebreak is left as it is now,
  // which can only matter between two members level on points.
  const before = getDisplayData({
    viewMode: "current",
    members: enriched.map((m) => ({ ...m, points: (m.points ?? 0) - m.weekPoints })),
  });
  const rankBefore = {};
  before.forEach((m, i) => { rankBefore[m.id] = i + 1; });

  const ranked = current.map((m, i) => {
    const rank = i + 1;
    return {
      memberId: m.id,
      rank,
      teamName: m.teamName,
      managerName: names[m.id],
      firstName: m.users?.first_name || null,
      avatarUrl: m.avatar_url || null,
      total: m.points ?? 0,
      weekPoints: m.weekPoints,
      // Positive is places gained.
      move: rankBefore[m.id] ? rankBefore[m.id] - rank : 0,
      isMe: m.user_id === viewerId,
      tripPlay: tripPlayByMember[m.id] || null,
    };
  });

  const moveItems = buildMoveItems({ moves, names, viewerMemberIds: ranked.filter((r) => r.isMe).map((r) => r.memberId), now });
  const captainItems = buildCaptainItems({ ranked, rowsByMember, week, gamesBySlug, teams });
  const me = ranked.find((r) => r.isMe);
  const myRow = me ? (rowsByMember[me.memberId] || []).find((r) => r.week === week) : null;
  return {
    week,
    table: trimTable(ranked),
    ticker: buildTicker(ranked, tripPlayByMember, moveItems, captainItems),
    myLineup: me ? buildMyLineup({ row: myRow, gamesBySlug, teams, now }) : null,
  };
}

// A captain worth a headline: at least this many points either way, which the
// double makes a team result of five or more. Only once its game has started.
export const CAPTAIN_SWING = 10;

// "Paul's captain Ohio State +22 · Final". Biggest swing first. A captain
// carrying the 5x Play is left to the 5x line, which already names the team.
export function buildCaptainItems({ ranked, rowsByMember, week, gamesBySlug = {}, teams = {} }) {
  const items = [];
  ranked.forEach((r) => {
    const row = (rowsByMember[r.memberId] || []).find((w) => w.week === week);
    const slug = row?.captain;
    if (!slug || row.trip_play_team === slug) return;
    const points = (row.team_points || {})[slug];
    if (typeof points !== "number" || Math.abs(points) < CAPTAIN_SWING) return;
    const game = gamesBySlug[slug];
    const info = getGameDisplayInfo(game || null, slug, teams);
    if (info.type !== "live" && info.type !== "completed") return;
    items.push({
      kind: "captain",
      text: `${r.isMe ? "Your" : `${r.managerName}'s`} captain ${teams?.[slug]?.school || slug}`,
      detail: signed(points),
      tone: points < 0 ? "neg" : "pos",
      when: info.type === "completed" ? "Final" : formatPeriod(game.period) || "Live",
      swing: Math.abs(points),
    });
  });
  return items.sort((a, b) => b.swing - a.swing).map(({ swing, ...item }) => item);
}

export const STARTER_SLOTS = 5;

// A team outside the teams map has only its slug: "stony-brook" reads as
// "Stony Brook".
function readableSlug(slug) {
  return slug.split("-").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// The viewer's five starting slots for the week, for the strip under the
// table. Points are the row's team_points, which already carry the captain
// and 5x multipliers, so the strip adds up to the week total the table shows.
// A team with no entry yet has not scored: null, shown as a dash, not zero.
export function buildMyLineup({ row, gamesBySlug = {}, teams = {}, now = new Date() }) {
  const starters = row?.starters || [];
  const teamPoints = row?.team_points || {};
  const slots = Array.from({ length: STARTER_SLOTS }, (_, i) => {
    const slug = starters[i] || null;
    if (!slug) return { slug: null };
    const game = gamesBySlug[slug] || null;
    const info = getGameDisplayInfo(game, slug, teams);
    return {
      slug,
      school: teams?.[slug]?.school || readableSlug(slug),
      points: Object.prototype.hasOwnProperty.call(teamPoints, slug) ? teamPoints[slug] : null,
      // A team outside the FBS teams map has no games row either, so it
      // would read as a bye. It is not one: the app simply does not track it.
      state: teams?.[slug] ? slotState(info, game, now) : "No game",
      live: info.type === "live",
      captain: row?.captain === slug,
      tripPlay: row?.trip_play_team === slug,
      fixture: teams?.[slug] ? buildFixture(info, game, slug, teams, now) : UNTRACKED,
      // For choosing which slot opens first.
      upcomingAt: teams?.[slug] && info.type === "upcoming" ? new Date(game.date).getTime() : null,
    };
  });
  return { slots, weekPoints: row?.points ?? 0, defaultPick: defaultPick(slots) };
}

// The slot whose fixture opens first: a game being played now, else the next
// to kick off, else the captain, else the first team in the lineup. null only
// when every slot is empty.
export function defaultPick(slots) {
  const index = (test) => slots.findIndex((s) => s.slug && test(s));
  const live = index((s) => s.live);
  if (live !== -1) return live;
  let next = -1;
  slots.forEach((s, i) => {
    if (s.slug && Number.isFinite(s.upcomingAt) && (next === -1 || s.upcomingAt < slots[next].upcomingAt)) next = i;
  });
  if (next !== -1) return next;
  const captain = index((s) => s.captain);
  if (captain !== -1) return captain;
  const first = index(() => true);
  return first === -1 ? null : first;
}

// A few characters under a logo: "Q3 24-10", "W 31-20", "7:30pm" today or
// the weekday otherwise, "Bye". buildGamesBySlug has already turned the
// scores to this team's side, so homeScore is always its own.
function slotState(info, game, now) {
  if (info.type === "bye-week") return "Bye";
  if (info.type === "interrupted") return info.display;
  if (info.type === "upcoming") return kickoffLabel(game.date, now);
  const mine = game.homeScore;
  const theirs = game.awayScore;
  if (info.type === "completed") return `${mine > theirs ? "W" : "L"} ${mine}-${theirs}`;
  const period = formatPeriod(game.period);
  return `${period ? `${period} ` : ""}${mine}-${theirs}`;
}

// The line a tapped slot opens: who, where, when or how it is going, and the
// spread. Read from the same game row and helpers as the slot itself.
const UNTRACKED = { opponent: null, title: "No game", detail: "Only FBS games are tracked" };

function buildFixture(info, game, slug, teams, now) {
  if (info.type === "bye-week") return { opponent: null, title: "Bye week", detail: "Bye week" };
  const oppSlug = game.awayTeam;
  const opp = teams?.[oppSlug];
  const record = opp?.currentSeason?.record || null;
  const spread = shouldShowSpread(info) ? getGameSpreadDisplay(game, slug) : null;
  let when;
  if (info.type === "live") {
    const clock = game.clock && game.clock !== "0:00" && game.clock !== "00:00" ? ` ${game.clock}` : "";
    when = `${formatPeriod(game.period)}${clock} \u00b7 ${game.homeScore}-${game.awayScore}`.trim();
  } else if (info.type === "completed") {
    when = `Final \u00b7 ${game.homeScore > game.awayScore ? "W" : "L"} ${game.homeScore}-${game.awayScore}`;
  } else if (info.type === "interrupted") {
    when = info.display;
  } else {
    when = kickoffFull(game.date, now);
  }
  return {
    opponent: oppSlug,
    title: `${game.isHome ? "vs" : "@"} ${opp?.school || oppSlug}`,
    record,
    when,
    live: info.type === "live",
    spread,
  };
}

const EASTERN = "America/New_York";

// "Today 7:30pm", or "Sat 12:00pm".
export function kickoffFull(date, now = new Date()) {
  const at = new Date(date);
  if (Number.isNaN(at.getTime())) return "Kickoff TBD";
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: EASTERN }).replace(/\s/g, "").toLowerCase();
  const day = (d) => d.toLocaleDateString("en-CA", { timeZone: EASTERN });
  const label = day(at) === day(now) ? "Today" : at.toLocaleDateString("en-US", { weekday: "short", timeZone: EASTERN });
  return `${label} ${time}`;
}

export function kickoffLabel(date, now = new Date()) {
  const at = new Date(date);
  if (Number.isNaN(at.getTime())) return "TBD";
  const day = (d) => d.toLocaleDateString("en-CA", { timeZone: EASTERN });
  if (day(at) === day(now)) {
    return at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: EASTERN }).replace(/\s/g, "").toLowerCase();
  }
  return at.toLocaleDateString("en-US", { weekday: "short", timeZone: EASTERN });
}

// What the ticker calls each manager: their first name, which is how a league
// talks about itself, with a last initial for anyone sharing a first name
// with someone else in the league. The team name is the fallback for a row
// with no first name, which is what an anonymized (deleted) account leaves.
export function managerNames(members) {
  const firstCounts = {};
  members.forEach((m) => {
    const first = (m.users?.first_name || "").trim();
    if (first) firstCounts[first.toLowerCase()] = (firstCounts[first.toLowerCase()] || 0) + 1;
  });
  const names = {};
  members.forEach((m) => {
    const first = (m.users?.first_name || "").trim();
    const lastInitial = (m.users?.last_name || "").trim().charAt(0).toUpperCase();
    if (!first) names[m.id] = m.team_name || "Unnamed Team";
    else if (firstCounts[first.toLowerCase()] > 1 && lastInitial) names[m.id] = `${first} ${lastInitial}.`;
    else names[m.id] = first;
  });
  return names;
}

export const MOVES_PER_LEAGUE = 5;
// How long a roster move stays news. Long enough that someone checking once a
// day, at any hour, sees yesterday's moves; gone the day after.
export const MOVES_WINDOW_MS = 36 * 60 * 60 * 1000;

// One ticker line per roster move. A swap is one move_history row with both
// columns set, so it reads as one line rather than an add and a drop.
export function buildMoveItems({ moves, names, viewerMemberIds = [], now = new Date() }) {
  // Filtered here as well as in the query, so a move ages out on a live
  // refresh without waiting for the next full load.
  const since = now.getTime() - MOVES_WINDOW_MS;
  return moves
    .filter((m) => names[m.member_id] && (m.added || m.dropped) && new Date(m.created_at).getTime() >= since)
    .slice(0, MOVES_PER_LEAGUE)
    .map((m) => {
      const who = viewerMemberIds.includes(m.member_id) ? "You" : names[m.member_id];
      let text;
      if (m.added && m.dropped) text = `${who} dropped ${m.dropped} for ${m.added}`;
      else if (m.added) text = `${who} added ${m.added}`;
      else text = `${who} dropped ${m.dropped}`;
      return { kind: "move", text, when: whenLabel(m.created_at, now) };
    });
}

// "12m", "5h" inside a day, the weekday after that. Moves older than the
// window never reach here, so a weekday cannot be a week out.
export function whenLabel(createdAt, now = new Date()) {
  const at = new Date(createdAt);
  const mins = Math.floor((now.getTime() - at.getTime()) / 60000);
  if (!Number.isFinite(mins)) return null;
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h`;
  return at.toLocaleDateString("en-US", { weekday: "short", timeZone: EASTERN });
}

// The top three and the viewer: six rows, always, so live cards side by side
// in the rail stay close in height. A viewer in the top five sees the top
// six, which already holds the managers either side of them. Lower down, the
// top three, a break, then the viewer between the managers just above and
// just below (the two above, for a viewer in last place).
//
// gapAfter is the index of the row the break follows; null when no places
// are skipped. cutAfter is the index of sixth place, under which the card
// draws the playoff line; null when sixth is not shown, or when the league is
// no bigger than the playoff field and the line would cut nobody.
export const TABLE_ROWS = 6;
const TABLE_TOP = 3;

export function trimTable(ranked) {
  const me = ranked.find((r) => r.isMe);
  let rows;
  let gapAfter = null;
  if (ranked.length <= TABLE_ROWS || !me || me.rank <= TABLE_ROWS - 1) {
    rows = ranked.slice(0, TABLE_ROWS);
  } else {
    const at = ranked.indexOf(me);
    const start = Math.min(at - 1, ranked.length - 3);
    rows = [...ranked.slice(0, TABLE_TOP), ...ranked.slice(start, start + 3)];
    gapAfter = TABLE_TOP - 1;
  }
  const sixth = rows.findIndex((r) => r.rank === PLAYOFF_TEAMS);
  const cutAfter = ranked.length > PLAYOFF_TEAMS && sixth !== -1 ? sixth : null;
  return { rows, gapAfter, cutAfter };
}

// Short lines, most interesting first: live 5x Plays, big captain swings,
// roster moves, places changed this week and the week's top score. On a
// gameday the live lines lead; midweek the bar is mostly moves. Where the viewer stands is left
// to the table right underneath, which already says it.
// At most MAX_HEADLINES, most important first. Each gets about six seconds on
// screen, so ten is a minute-long lap; past that nobody waits for the start to
// come round, and the least important fall off the end.
export const MAX_HEADLINES = 10;

export function buildTicker(ranked, tripPlayByMember = {}, moveItems = [], captainItems = []) {
  const items = [];

  ranked.forEach((r) => {
    const play = tripPlayByMember[r.memberId];
    if (!play) return;
    // "[5x] Sam · Ohio State", then the score once it kicks off or the
    // kickoff before. The chip says 5x, so the words do not.
    items.push({
      kind: "tripPlay",
      text: `${r.isMe ? "You" : r.managerName} \u00b7 ${play.school}`,
      detail: play.score || null,
      when: play.score ? null : play.kickoff || null,
    });
  });

  items.push(...captainItems);
  items.push(...moveItems);

  // Only movement this week has anything to report, and a league where every
  // member's week is still zero has not moved at all.
  const anyPoints = ranked.some((r) => r.weekPoints !== 0);
  if (anyPoints) {
    ranked
      .filter((r) => r.move !== 0)
      .sort((a, b) => Math.abs(b.move) - Math.abs(a.move) || a.rank - b.rank)
      .slice(0, 3)
      .forEach((r) => {
        items.push({
          kind: r.move > 0 ? "up" : "down",
          text: `${r.isMe ? "You" : r.managerName} ${r.move > 0 ? "up" : "down"} to ${ordinal(r.rank)}`,
        });
      });

    const best = [...ranked].sort((a, b) => b.weekPoints - a.weekPoints)[0];
    if (best && best.weekPoints > 0) {
      items.push({ kind: "top", text: `Top score this week: ${best.isMe ? "you" : best.managerName}`, detail: signed(best.weekPoints) });
    }
  }

  return items.slice(0, MAX_HEADLINES);
}

export function signed(n) {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return "0";
}
